/**
 * 图片优化工具
 * 用于提升图片加载性能，改善LCP和CLS指标
 */

/**
 * 预加载重要图片
 * @param urls 图片URL数组
 * @param priority 是否高优先级加载（fetchpriority属性）
 */
export function preloadImages(urls: string[], priority: boolean = false): void {
  urls.forEach(url => {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = url
    if (priority) {
      link.setAttribute('fetchpriority', 'high')
    }
    document.head.appendChild(link)
  })
}

/**
 * 延迟加载非关键图片
 * 当浏览器空闲时加载图片
 * @param urls 图片URL数组
 */
export function lazyLoadImages(urls: string[]): void {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      urls.forEach(url => {
        new Image().src = url
      })
    })
  } else {
    // 降级处理
    setTimeout(() => {
      urls.forEach(url => {
        new Image().src = url
      })
    }, 1000)
  }
}

/**
 * 检查并使用WebP格式
 * @param url 原始图片URL
 * @returns WebP格式的URL或原始URL
 */
export function getOptimizedImageUrl(url: string): string {
  if (!url) return url

  // 检查浏览器是否支持WebP
  const supportsWebP = localStorage.getItem('supports_webp')

  if (supportsWebP === 'true' && !url.endsWith('.webp') && !url.endsWith('.svg')) {
    // 如果URL已经包含查询参数，添加&format=webp，否则添加?format=webp
    return url.includes('?') ? `${url}&format=webp` : `${url}?format=webp`
  }

  return url
}

/**
 * 检测浏览器WebP支持情况
 */
export function detectWebPSupport(): void {
  const webP = new Image()
  webP.onload = function () {
    const result = webP.width > 0 && webP.height > 0
    localStorage.setItem('supports_webp', result ? 'true' : 'false')
  }
  webP.onerror = function () {
    localStorage.setItem('supports_webp', 'false')
  }
  webP.src = 'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA=='
}

/**
 * 获取图片尺寸，以确保页面布局稳定
 * @param url 图片URL
 * @returns Promise，包含图片宽高
 */
export function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = function () {
      resolve({
        width: img.width,
        height: img.height,
      })
    }
    img.onerror = function () {
      reject(new Error('Failed to load image'))
    }
    img.src = url
  })
}

/**
 * 计算图片占位区域，避免CLS
 * @param url 图片URL
 * @param containerWidth 容器宽度
 * @returns 计算得到的高度
 */
export async function calculateImagePlaceholderHeight(
  url: string,
  containerWidth: number,
): Promise<number> {
  try {
    const { width, height } = await getImageDimensions(url)
    const aspectRatio = height / width
    return containerWidth * aspectRatio
  } catch (error) {
    console.error('Error calculating image height:', error)
    return 0
  }
}

/**
 * 生成响应式图片URL
 * @param url 原始图片URL
 * @param width 目标宽度
 * @returns 处理后的响应式图片URL
 */
export function getResponsiveImageUrl(url: string, width: number): string {
  if (!url) return url

  // 检查URL是否已经包含宽度参数
  if (url.includes('width=') || url.includes('w=')) {
    return url
  }

  // 添加宽度参数
  return url.includes('?') ? `${url}&w=${width}` : `${url}?w=${width}`
}

/**
 * 生成响应式srcset属性
 * @param url 原始图片URL
 * @param sizes 宽度数组，例如[320, 640, 960, 1280]
 * @returns srcset属性值
 */
export function generateSrcSet(url: string, sizes: number[] = [320, 640, 960, 1280]): string {
  if (!url) return ''

  return sizes.map(size => `${getResponsiveImageUrl(url, size)} ${size}w`).join(', ')
}

/**
 * 初始化图片优化
 * 在应用启动时调用一次
 */
export function initImageOptimization(): void {
  // 检测WebP支持
  detectWebPSupport()

  // 查找并修复所有img标签的CLS问题
  document.addEventListener('DOMContentLoaded', () => {
    const images = document.querySelectorAll('img:not([width]):not([height])')

    images.forEach(img => {
      // 为没有宽高的图片添加宽高比
      const imgElement = img as HTMLImageElement
      if (
        imgElement.src &&
        !imgElement.getAttribute('width') &&
        !imgElement.getAttribute('height')
      ) {
        imgElement.style.aspectRatio = 'auto'
      }
    })
  })
}
